// carriers.js - What a person is allowed to pick when buying a label, in words
// they can read, mapped to the strings the provider actually wants.
//
// This file exists because the two providers do not share a vocabulary and
// neither of them speaks English. The same overnight envelope is
// `FEDEX_2_DAY` with `FedExEnvelope` at EasyPost and `usps_first_class_mail`
// with `Envelope10` at Stamps, and nobody on a bench should have to remember
// either. Every dropdown in the dashboard is built from here, so the label says
// "FedEx 2Day" and the request carries `FEDEX_2_DAY`.
//
// It is deliberately not an enum on the server. Carriers add services and retire
// them without asking us, and a hard list would refuse a service that works on
// the day it appears. So the server takes any string, this file lists the ones
// worth offering, and every control keeps a "Something else" escape that reveals
// a text box. The dropdown is there to save knowledge, not to withhold options.
//
// LEGEND
//   1. PROVIDERS      who prints it, and what each is for
//   2. CATALOGUE      provider, carrier, service and package, in plain English
//   3. carriersFor()  the carriers one provider can print
//   4. servicesFor()  the services one carrier offers, with our default first
//   5. packagesFor()  the packagings that carrier accepts
//   6. describe()     a stored value turned back into words, for a summary line
//   7. equivalentFor() the same choice said in another provider's words

////////// 1. PROVIDERS //////////

export const PROVIDERS = [
  {
    value: "easypost",
    label: "EasyPost",
    note: "Parcels on our carrier accounts. Rate shops when no service is named.",
  },
  {
    value: "stamps",
    label: "Stamps.com",
    note: "Prints postage straight onto a flat. Cheapest by far for an empty kit.",
  },
];

////////// 2. THE CATALOGUE //////////
// `code` is what goes on the wire. `label` is what a person reads. The first
// service and the first package in each list are the sensible default for this
// business, not the carrier's own first.

export const CATALOGUE = {
  easypost: {
    label: "EasyPost",
    carriers: [
      {
        code: "FedEx",
        label: "FedEx",
        services: [
          { code: "FEDEX_2_DAY", label: "FedEx 2Day", note: "What every return label uses" },
          { code: "STANDARD_OVERNIGHT", label: "Standard Overnight" },
          { code: "PRIORITY_OVERNIGHT", label: "Priority Overnight" },
          { code: "FIRST_OVERNIGHT", label: "First Overnight", note: "Earliest, and the dearest" },
          { code: "FEDEX_2_DAY_AM", label: "FedEx 2Day AM" },
          { code: "FEDEX_EXPRESS_SAVER", label: "Express Saver, three day" },
          { code: "FEDEX_GROUND", label: "FedEx Ground" },
          { code: "GROUND_HOME_DELIVERY", label: "Ground Home Delivery", note: "Residential ground" },
        ],
        packages: [
          { code: "FedExEnvelope", label: "FedEx envelope", note: "The return label default" },
          { code: "FedExPak", label: "FedEx Pak" },
          { code: "FedExSmallBox", label: "Small box" },
          { code: "FedExMediumBox", label: "Medium box" },
          { code: "FedExLargeBox", label: "Large box" },
          { code: "FedExTube", label: "Tube" },
        ],
      },
      {
        code: "UPS",
        label: "UPS",
        services: [
          { code: "Ground", label: "UPS Ground" },
          { code: "2ndDayAir", label: "2nd Day Air" },
          { code: "3DaySelect", label: "3 Day Select" },
          { code: "NextDayAirSaver", label: "Next Day Air Saver" },
          { code: "NextDayAir", label: "Next Day Air" },
        ],
        packages: [
          { code: "UPSLetter", label: "UPS letter" },
          { code: "SmallExpressBox", label: "Small express box" },
          { code: "MediumExpressBox", label: "Medium express box" },
          { code: "LargeExpressBox", label: "Large express box" },
          { code: "Pak", label: "Pak" },
          { code: "Tube", label: "Tube" },
        ],
      },
      {
        code: "USPS",
        label: "USPS",
        services: [
          { code: "First", label: "First Class Mail", note: "What the empty kit goes out as" },
          { code: "GroundAdvantage", label: "Ground Advantage" },
          { code: "Priority", label: "Priority Mail" },
          { code: "Express", label: "Priority Mail Express" },
          { code: "ParcelSelect", label: "Parcel Select" },
          { code: "MediaMail", label: "Media Mail" },
        ],
        packages: [
          { code: "Parcel", label: "Own packaging" },
          { code: "FlatRateEnvelope", label: "Flat rate envelope" },
          { code: "FlatRatePaddedEnvelope", label: "Flat rate padded envelope" },
          { code: "SmallFlatRateBox", label: "Small flat rate box" },
          { code: "MediumFlatRateBox", label: "Medium flat rate box" },
          { code: "LargeFlatRateBox", label: "Large flat rate box" },
        ],
      },
    ],
  },

  // Stamps prints USPS and nothing else, and its strings look nothing like the
  // ones EasyPost uses for the same services.
  stamps: {
    label: "Stamps.com",
    carriers: [
      {
        code: "USPS",
        label: "USPS",
        services: [
          { code: "usps_first_class_mail", label: "First Class Mail", note: "What the empty kit goes out as" },
          { code: "usps_ground_advantage", label: "Ground Advantage" },
          { code: "usps_priority_mail", label: "Priority Mail" },
          { code: "usps_priority_mail_express", label: "Priority Mail Express" },
          { code: "usps_media_mail", label: "Media Mail" },
        ],
        packages: [
          { code: "Envelope10", label: "No. 10 envelope", note: "The kit mailer" },
          { code: "Envelope6x9", label: "6 by 9 envelope" },
          { code: "Envelope9x12", label: "9 by 12 envelope" },
          { code: "FlatRateEnvelope", label: "Flat rate envelope" },
          { code: "FlatRatePaddedEnvelope", label: "Flat rate padded envelope" },
        ],
      },
    ],
  },
};

////////// 3. CARRIERS //////////

export function carriersFor(provider) {
  return CATALOGUE[provider]?.carriers || [];
}

////////// 4. SERVICES //////////
// An empty carrier means "we have not chosen one", which on EasyPost is a
// legitimate answer that prices everything. There is nothing to list until a
// carrier is named, and saying so beats an empty dropdown.

export function servicesFor(provider, carrier) {
  return carriersFor(provider).find((entry) => entry.code === carrier)?.services || [];
}

////////// 5. PACKAGES //////////

export function packagesFor(provider, carrier) {
  return carriersFor(provider).find((entry) => entry.code === carrier)?.packages || [];
}

////////// 6. BACK INTO WORDS //////////
// For the line that summarises a template, and for anywhere a stored code has
// to be shown to somebody. An unknown code is returned as it was stored rather
// than hidden, because a value we do not recognise is exactly the one somebody
// needs to see.

export function describe(provider, carrier, service, packaging) {
  const carrierEntry = carriersFor(provider).find((entry) => entry.code === carrier);
  const named = (list, code) => list.find((entry) => entry.code === code)?.label || code;

  return [
    carrierEntry?.label || carrier,
    service ? named(carrierEntry?.services || [], service) : "cheapest available",
    packaging ? named(carrierEntry?.packages || [], packaging) : null,
  ].filter(Boolean).join(", ");
}

////////// 7. THE SAME PRODUCT, THE OTHER PROVIDER //////////
// Both providers sell USPS, so changing who prints a label is not a reason to
// throw away what somebody already chose. First Class Mail at Stamps and Ground
// Advantage at EasyPost are the same envelope going to the same place; only the
// string is different.
//
// This is the same knowledge as `STAMPS_TO_EASYPOST` in
// `server/src/lib/shipping.js`, which is what the server's fallback uses when
// Stamps is not connected. The two have to agree, because otherwise the dropdown
// would promise one service and the fallback would buy another. A smoke check
// reads this file and fails if they drift.
//
// One row per product, and every row is the same product on both sides. Nothing
// here maps two services onto one: changing providers must never change what is
// being bought, only what it is called, so a row that cannot be paired honestly
// does not belong in this table at all.
const SAME_PRODUCT = [
  { stamps: "usps_first_class_mail", easypost: "First" },
  { stamps: "usps_ground_advantage", easypost: "GroundAdvantage" },
  { stamps: "usps_priority_mail", easypost: "Priority" },
  { stamps: "usps_priority_mail_express", easypost: "Express" },
  { stamps: "usps_media_mail", easypost: "MediaMail" },
];

// The plain envelopes are deliberately absent. EasyPost sells parcels, not
// letter postage, so there is nothing there that means "a No. 10 envelope with a
// stamp on it" and the honest answer is our own packaging.
const SAME_PACKAGE = [
  { stamps: "FlatRateEnvelope", easypost: "FlatRateEnvelope" },
  { stamps: "FlatRatePaddedEnvelope", easypost: "FlatRatePaddedEnvelope" },
];

const translate = (table, code, provider) => {
  const from = provider === "stamps" ? "easypost" : "stamps";
  return table.find((row) => row[from] === code)?.[provider] || "";
};

// What `plan` becomes when `provider` prints it instead. Anything the new
// provider already understands is left exactly as it is.
export function equivalentFor(provider, { carrier = "", service = "", package: packaging = "" } = {}) {
  const carriers = carriersFor(provider);
  // Stamps prints USPS and nothing else, so its carrier is not a question. An
  // empty carrier on EasyPost is a real answer: price everybody.
  const nextCarrier = carriers.some((entry) => entry.code === carrier)
    ? carrier
    : provider === "stamps" ? carriers[0]?.code || "" : "";

  const services = servicesFor(provider, nextCarrier);
  const packages = packagesFor(provider, nextCarrier);
  const has = (list, code) => Boolean(code) && list.some((entry) => entry.code === code);

  // Stamps prices one service rather than quoting a list, so leaving it blank
  // there is not an option and it takes the sensible default instead.
  const fallback = (list) => (provider === "stamps" ? list[0]?.code || "" : "");

  return {
    carrier: nextCarrier,
    service: has(services, service) ? service : translate(SAME_PRODUCT, service, provider) || fallback(services),
    package: has(packages, packaging) ? packaging : translate(SAME_PACKAGE, packaging, provider) || fallback(packages),
  };
}
